import { useEffect, useMemo, useRef } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopilotKitAgentContext } from '@/components/CopilotKitProvider/CopilotKitAgentContext';
import { useStabilized } from '@/hooks/useStabilized';
import { useQuickPromptsForPage } from '@/hooks/aiChat/useQuickPromptsForPage';
import { useGettingStartedCompletionState } from '@/hooks/useGettingStartedCompletionState';
import {
  MAX_QUICK_PROMPTS,
  getGettingStartedQuickPrompts,
} from '@/constants/quickPrompts';
import type { QuickPrompt } from '@/constants/quickPrompts';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { ROUTES } from '@/constants/routes';

type UseCopilotKitQuickPromptsOptions = {
  /**
   * When false, quick prompts are hidden and not synced to CopilotKit suggestions
   * (e.g. drawer is closed). Default true for full-page chat.
   */
  isVisible?: boolean;
};

type UseCopilotKitQuickPromptsResult = {
  quickPrompts: QuickPrompt[];
  showQuickPrompts: boolean;
};

/**
 * Shared logic for quick prompts in CopilotKit chat (drawer and full page).
 * Computes whether the user has sent a message, fetches page-specific prompts,
 * syncs them to CopilotKit suggestions, and determines if the quick prompts UI
 * should be shown.
 */
export function useCopilotKitQuickPrompts(
  options: UseCopilotKitQuickPromptsOptions = {}
): UseCopilotKitQuickPromptsResult {
  const { isVisible = true } = options;
  const { t, i18n } = useTranslation('ai-chat');
  const { messages, setSuggestions, isLoading } = useCopilotKitAgentContext();
  const stableIsLoading = useStabilized(isLoading);

  // Hold the latest setSuggestions in a ref to avoid it as a useEffect dependency.
  // CopilotKit does not memoize this function, so including it directly causes an
  // infinite loop: new reference → effect runs → state update → re-render → new reference.
  const setSuggestionsRef = useRef(setSuggestions);
  setSuggestionsRef.current = setSuggestions;

  const hasUserSentMessage = messages.some(m => m.role === 'user');

  const { itHubDashboardGettingStarted } = useFlags();
  const location = useLocation();
  const isOnDashboard = location.pathname === ROUTES.DASHBOARD;

  const isSuperAdmin = useIsSuperAdmin();

  const { incompleteStepKeys, isLoading: gsStateLoading } =
    useGettingStartedCompletionState({
      enabled: !!itHubDashboardGettingStarted && isOnDashboard && isSuperAdmin,
    });

  const incompleteStepKeysKey = useMemo(
    () => JSON.stringify(incompleteStepKeys),
    [incompleteStepKeys]
  );

  const gsQuickPrompts = useMemo(
    () => {
      if (!isOnDashboard || gsStateLoading || incompleteStepKeys.length === 0) {
        return undefined;
      }
      return getGettingStartedQuickPrompts(incompleteStepKeys, t);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- incompleteStepKeysKey is the semantic fingerprint; incompleteStepKeys is read when the key changes
    [isOnDashboard, gsStateLoading, incompleteStepKeysKey, t]
  );

  const quickPrompts = useQuickPromptsForPage({
    isShowingEmptyState: isVisible && !hasUserSentMessage,
    overridePrompts: gsQuickPrompts,
  });

  const quickPromptsKey = useMemo(
    () =>
      JSON.stringify(
        quickPrompts
          .slice(0, MAX_QUICK_PROMPTS)
          .map(p => ({ title: p.label, message: p.message }))
      ),
    [quickPrompts]
  );
  const lastSyncedSuggestionsKeyRef = useRef('');

  // Sync page-specific quick prompts into CopilotKit's built-in suggestions (manual mode).
  // Only sync when visible so the drawer doesn't overwrite full-page chat suggestions when both are mounted.
  // Include messages.length so we re-run when conversation is cleared (messages -> [] -> [WELCOME_MESSAGE]).
  useEffect(() => {
    if (!isVisible) return;
    if (hasUserSentMessage) {
      if (lastSyncedSuggestionsKeyRef.current === '[]') return;
      lastSyncedSuggestionsKeyRef.current = '[]';
      setSuggestionsRef.current([]);
      return;
    }
    if (quickPromptsKey === lastSyncedSuggestionsKeyRef.current) return;
    lastSyncedSuggestionsKeyRef.current = quickPromptsKey;
    setSuggestionsRef.current(JSON.parse(quickPromptsKey));
  }, [
    isVisible,
    hasUserSentMessage,
    quickPromptsKey,
    messages.length,
    i18n.language,
  ]);

  const showQuickPrompts =
    isVisible &&
    !hasUserSentMessage &&
    !stableIsLoading &&
    quickPrompts.length > 0;

  return { quickPrompts, showQuickPrompts };
}
